/**
 * Admission Forms Tab — client admission paperwork (consents, rights, agreements)
 * completed by the nurse on the agency's PDF forms. Separate from the clinical assessment.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import StartAssessmentDialog from "@/components/assessments/StartAssessmentDialog";
import AssessmentFiller from "@/components/assessments/AssessmentFiller";
import AssessmentList from "@/components/assessments/AssessmentList";
import { FileSignature, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdmissionFormsTab({ clientId, clientName, onCompleted }) {
  const { user } = useAuth();
  const canEdit = ["rn", "admin"].includes((user?.role || "").toLowerCase());
  const [items, setItems] = useState(null);
  const [starting, setStarting] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/assessments`, { params: { client_id: clientId, group: "admission_forms" } });
      setItems(r.data.assessments);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load admission forms");
      setItems([]);
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const closeFiller = useCallback(() => setOpenId(null), []);

  const done = (items || []).filter((a) => a.status === "completed").length;
  const total = items?.length || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div data-testid="client-admission-forms-tab" className="space-y-6">
      <div className="form-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-violet-600" /> Admission Forms
            </h3>
            <p className="text-sm text-slate-500">Consents, rights and agreements the nurse completes with the client at admission.</p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={() => setStarting(true)} className="gap-2 bg-violet-600 hover:bg-violet-700" data-testid="new-admission-forms-btn">
              <Plus className="w-4 h-4" /> Start admission forms
            </Button>
          )}
        </div>

        {total > 0 && (
          <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2" data-testid="admission-forms-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-violet-900">Admission forms: {done} of {total} completed</span>
              <span className="text-xs text-violet-700">{pct}%</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-violet-100 overflow-hidden">
              <div className="h-full bg-violet-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <AssessmentList
          items={items}
          canEdit={canEdit}
          onOpen={(a) => setOpenId(a.id)}
          onChanged={load}
          emptyText={canEdit ? "No admission forms yet — click “Start admission forms” to pick the packet for this client." : "No admission forms recorded yet."}
        />
      </div>

      <StartAssessmentDialog open={starting} onClose={() => setStarting(false)} clientId={clientId} clientName={clientName} defaultType="admission_forms" lockType onStarted={(first) => { load(); if (first) setOpenId(first.id); }} />
      {openId && <AssessmentFiller assessmentId={openId} onClose={closeFiller} onCompleted={() => { load(); onCompleted?.(); }} />}
    </div>
  );
}
